import type { RecommendationRoute, RecommendationRule } from "../content/types.js";
import type { ClassificationResult } from "./engine.types.js";

/** What the person declares blocks them, gathered on the pre-screen/reaction screens. */
export type DeclaredBlocker = "recognize" | "rebuild" | "practice" | null;

/** Reaction to the result, screen 6 of the flow. */
export type ResultReaction = "fits" | "partial" | "not" | "need_more";

/**
 * Never recommends by pattern number — only by declared blocker and result
 * state, per the reconciled spec §5. Inner Reset can only ever ride along
 * as `secondaryCta` on the inner_architecture route; the return type has no
 * fourth route value, so it cannot become a third primary option by
 * construction.
 */
export function recommend(
  result: ClassificationResult,
  blocker: DeclaredBlocker,
  reaction: ResultReaction,
): RecommendationRule {
  const openOrRejected =
    result.state === "open" ||
    result.state === "insufficient" ||
    reaction === "not" ||
    reaction === "need_more";

  if (openOrRejected) {
    return {
      route: "keep_investigating",
      reason:
        "Don't force a type. Hold both hypotheses and watch for what separates them.",
      primaryCta: "Save the map and watch",
      ruleId: "REC-INVESTIGATE",
    };
  }

  if (blocker === "practice") {
    return {
      route: "inner_voice",
      reason:
        "You don't need another explanation. You need to catch it in a real situation and try one move.",
      primaryCta: "Open Inner Voice",
      ruleId: "REC-VOICE",
    };
  }

  const consistentEnough = result.consistency === "alta" || result.consistency === "moderada";
  if (blocker === "rebuild" || consistentEnough) {
    return {
      route: "inner_architecture",
      reason: "Naming the pattern doesn't change what happens next. That part is what Inner Architecture works on.",
      primaryCta: "See the system",
      secondaryCta: "Try 7 days (Inner Reset)",
      ruleId: "REC-ARCHITECTURE",
    };
  }

  return {
    route: "keep_investigating",
    reason: "Don't force a type. Hold both hypotheses and watch for what separates them.",
    primaryCta: "Save the map and watch",
    ruleId: "REC-INVESTIGATE-DEFAULT",
  };
}

export function assertNoStrayRoute(route: RecommendationRoute): void {
  const allowed: RecommendationRoute[] = ["inner_architecture", "inner_voice", "keep_investigating"];
  if (!allowed.includes(route)) {
    throw new Error(`recommend: "${route}" is not a valid primary route`);
  }
}
